/**
 * Debounce utility for performance optimization
 * 
 * Creates a debounced function that delays invoking func until after wait milliseconds 
 * have elapsed since the last time the debounced function was invoked.
 * 
 * @param func The function to debounce
 * @param wait The number of milliseconds to delay
 * @returns A debounced version of the function
 */

/**
 * Debounce manager for handling multiple debounced operations
 */
export class DebounceManager {
  private timers = new Map<string, NodeJS.Timeout>();

  /**
   * Debounce a function call with a specific key
   * @param key Unique identifier for this debounce operation
   * @param fn Function to debounce
   * @param delay Delay in milliseconds
   */
  async debounce<T>(
    key: string,
    fn: () => Promise<T>,
    delay: number
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      // Clear existing timer
      const existingTimer = this.timers.get(key);
      if (existingTimer) {
        clearTimeout(existingTimer);
      }

      // Set new timer
      const timer = setTimeout(async () => {
        this.timers.delete(key);
        try {
          const result = await fn();
          resolve(result);
        } catch (error) {
          reject(error);
        }
      }, delay);

      this.timers.set(key, timer);
    });
  }

  /**
   * Cancel a pending debounced operation
   * @param key The key of the operation to cancel
   */
  cancel(key: string): void {
    const timer = this.timers.get(key);
    if (timer) {
      clearTimeout(timer);
      this.timers.delete(key);
    }
  }

  /**
   * Cancel all pending debounced operations
   */
  cancelAll(): void {
    this.timers.forEach(timer => clearTimeout(timer));
    this.timers.clear();
  }

  /**
   * Check if a key has a pending operation
   * @param key The key to check
   */
  isPending(key: string): boolean {
    return this.timers.has(key);
  }
}
export function debounce<T extends (...args: unknown[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeoutId: NodeJS.Timeout | null = null;
  
  return function debounced(...args: Parameters<T>) {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
    }
    
    timeoutId = setTimeout(() => {
      func(...args);
      timeoutId = null;
    }, wait);
  };
}

/**
 * Debounce with promise support
 * 
 * Creates a debounced function that returns a promise, useful for async operations
 * 
 * @param func The async function to debounce
 * @param wait The number of milliseconds to delay
 * @returns A debounced version of the async function
 */
export function debounceAsync<T extends (...args: unknown[]) => Promise<any>>(
  func: T,
  wait: number
): (...args: Parameters<T>) => Promise<Awaited<ReturnType<T>>> {
  let timeoutId: NodeJS.Timeout | null = null;
  let resolveList: Array<{
    resolve: (value: Awaited<ReturnType<T>>) => void;
    reject: (reason?: unknown) => void;
  }> = [];
  
  return function debounced(...args: Parameters<T>): Promise<Awaited<ReturnType<T>>> {
    return new Promise((resolve, reject) => {
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
      
      resolveList.push({ resolve, reject });
      
      timeoutId = setTimeout(async () => {
        try {
          const result = await func(...args);
          resolveList.forEach(({ resolve }) => resolve(result));
        } catch (error) {
          resolveList.forEach(({ reject }) => reject(error));
        } finally {
          resolveList = [];
          timeoutId = null;
        }
      }, wait);
    });
  };
}

/**
 * Debounce with leading edge support
 * 
 * Creates a debounced function that invokes func on the leading edge of the timeout
 * 
 * @param func The function to debounce
 * @param wait The number of milliseconds to delay
 * @param options Options object with leading flag
 * @returns A debounced version of the function
 */
export function debounceWithOptions<T extends (...args: unknown[]) => any>(
  func: T,
  wait: number,
  options: { leading?: boolean; trailing?: boolean } = {}
): (...args: Parameters<T>) => void {
  let timeoutId: NodeJS.Timeout | null = null;
  let lastArgs: Parameters<T> | null = null;
  let lastThis: any = null;
  let result: ReturnType<T>;
  let lastCallTime: number | null = null;
  let lastInvokeTime = 0;
  
  const leading = options.leading ?? false;
  const trailing = options.trailing ?? true;
  
  function invokeFunc(time: number) {
    const args = lastArgs!;
    const thisArg = lastThis;
    
    lastArgs = lastThis = null;
    lastInvokeTime = time;
    result = func.apply(thisArg, args);
    return result;
  }
  
  function leadingEdge(time: number) {
    lastInvokeTime = time;
    timeoutId = setTimeout(timerExpired, wait);
    return leading ? invokeFunc(time) : result;
  }
  
  function timerExpired() {
    const time = Date.now();
    if (shouldInvoke(time)) {
      return trailingEdge(time);
    }
    timeoutId = setTimeout(timerExpired, remainingWait(time));
  }
  
  function trailingEdge(time: number) {
    timeoutId = null;
    if (trailing && lastArgs) {
      return invokeFunc(time);
    }
    lastArgs = lastThis = null;
    return result;
  }
  
  function shouldInvoke(time: number) {
    const timeSinceLastCall = lastCallTime === null ? 0 : time - lastCallTime;
    const _timeSinceLastInvoke = time - lastInvokeTime;
    
    return lastCallTime === null ||
      timeSinceLastCall >= wait ||
      timeSinceLastCall < 0 ||
      timeSinceLastInvoke >= wait;
  }
  
  function remainingWait(time: number) {
    const timeSinceLastCall = time - (lastCallTime ?? 0);
    const _timeSinceLastInvoke = time - lastInvokeTime;
    const timeWaiting = wait - timeSinceLastCall;
    
    return timeWaiting;
  }
  
  return function debounced(this: unknown, ...args: Parameters<T>) {
    const time = Date.now();
    const isInvoking = shouldInvoke(time);
    
    lastArgs = args;
    // eslint-disable-next-line @typescript-eslint/no-this-alias -- debounce must capture the call-site `this` to re-apply it on the deferred invocation
    lastThis = this;
    lastCallTime = time;
    
    if (isInvoking) {
      if (timeoutId === null) {
        return leadingEdge(lastCallTime);
      }
    }
    if (timeoutId === null) {
      timeoutId = setTimeout(timerExpired, wait);
    }
    return result;
  };
}